import { AlertTriangle, AlertCircle, Clock, ServerOff, MessageSquareOff, Frown, ShoppingCart, PackageCheck, Reply } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

export type EscalationTrigger = 'keyword' | 'customer_reply' | 'frustration' | 'ai_error' | 'no_response' | 'service_issue' | 'no_message' | 'purchase_intent' | 'order_submitted' | 'flow_completion';

interface EscalationBadgeProps {
  trigger: EscalationTrigger;
  animate?: boolean;
  className?: string;
}

const triggerConfig = {
  keyword: {
    icon: AlertTriangle,
    translationKey: 'keyword',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800'
  },
  customer_reply: {
    icon: Reply,
    translationKey: 'customerReply',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-700'
  },
  frustration: {
    icon: Frown,
    translationKey: 'frustration',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800'
  },
  ai_error: {
    icon: AlertCircle,
    translationKey: 'aiError',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800'
  },
  no_response: {
    icon: Clock,
    translationKey: 'noResponse',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800'
  },
  service_issue: {
    icon: ServerOff,
    translationKey: 'serviceIssue',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800'
  },
  no_message: {
    icon: MessageSquareOff,
    translationKey: 'noMessage',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 border-gray-200 dark:border-gray-700'
  },
  purchase_intent: {
    icon: ShoppingCart,
    translationKey: 'purchaseIntent',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800'
  },
  order_submitted: {
    icon: PackageCheck,
    translationKey: 'orderSubmitted',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800'
  },
  flow_completion: {
    icon: PackageCheck,
    translationKey: 'flowCompletion',
    className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800'
  }
};

export function EscalationBadge({ trigger, animate = false, className }: EscalationBadgeProps) {
  const { t } = useTranslation('conversations');
  // V1273: Fallback for unknown trigger types to prevent crashes
  const config = triggerConfig[trigger] || triggerConfig.keyword;
  const IconComponent = config.icon;

  return (
    <Badge
      variant="outline"
      className={`
        flex items-center gap-1 text-xs px-1.5 py-0.5 h-5
        ${config.className}
        ${animate ? 'animate-pulse' : ''}
        ${className}
      `}
    >
      <IconComponent className="h-3 w-3" />
      <span className="font-medium">{t(`escalation.badge.${config.translationKey}`)}</span>
    </Badge>
  );
}
